/**
 * Level & radius progression. Pure functions, no store dependency.
 * Level 1 = 5 km, +2 km per level, cap at Level 20 = 43 km.
 * Level-up cost follows cost = 100_000 * level^1.6 (rounded to integer €).
 */

export const LEVEL_MIN = 1;
export const LEVEL_MAX = 20;
export const RADIUS_BASE_KM = 5;
export const RADIUS_PER_LEVEL_KM = 2;
export const LEVEL_UP_BASE_COST = 100_000;
export const LEVEL_UP_EXPONENT = 1.6;

export function clampLevel(level: number): number {
  return Math.max(LEVEL_MIN, Math.min(LEVEL_MAX, Math.floor(level)));
}

/** Radius (km) at a given level. */
export function getRadiusKm(level: number): number {
  const l = clampLevel(level);
  return RADIUS_BASE_KM + (l - 1) * RADIUS_PER_LEVEL_KM;
}

/**
 * Cost (€, integer) to advance from `fromLevel` to `fromLevel + 1`.
 * Formula: 100_000 * fromLevel^1.6 (source-level indexed).
 * Produces the user-specified anchor values:
 *   L1 -> L2 = 100_000
 *   L2 -> L3 ≈ 303_143
 *   L4 -> L5 ≈ 918_959
 *   L9 -> L10 ≈ 3_481_075
 *   L19 -> L20 ≈ 10_449_003
 * Returns Infinity if already at cap.
 */
export function getLevelUpCost(fromLevel: number): number {
  const l = clampLevel(fromLevel);
  if (l >= LEVEL_MAX) return Infinity;
  return Math.round(LEVEL_UP_BASE_COST * Math.pow(l, LEVEL_UP_EXPONENT));
}

export function isAtCap(level: number): boolean {
  return clampLevel(level) >= LEVEL_MAX;
}

/** Pre-computed snapshot for UI tables / debugging. */
export const LEVEL_TABLE: ReadonlyArray<{ level: number; radiusKm: number; upgradeCost: number }> =
  Array.from({ length: LEVEL_MAX }, (_, i) => {
    const level = i + 1;
    return {
      level,
      radiusKm: getRadiusKm(level),
      upgradeCost: getLevelUpCost(level),
    };
  });
